import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { catalogApi, controlMatrixApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { PageHeader } from '../components/PageHeader';

type FieldConfig = {
  fieldId: string;
  fieldName?: string;
  fieldType?: string;
  options?: any[];
  cardNumber?: number;
  order?: number;
  required?: boolean;
  visible?: boolean;
};

type CatalogField = {
  fieldId: string;
  fieldName?: string;
  fieldType?: string;
  options?: any[];
  category?: string;
};

const getDefaultFields = (): FieldConfig[] => {
  return [
    {
      fieldId: 'control_id',
      fieldName: 'Control ID',
      fieldType: 'text',
      cardNumber: 1,
      order: 1,
      required: true,
      visible: true,
    },
    {
      fieldId: 'control_title',
      fieldName: 'Control Title',
      fieldType: 'text',
      cardNumber: 1,
      order: 2,
      required: true,
      visible: true,
    },
    {
      fieldId: 'control_definition',
      fieldName: 'Control Definition',
      fieldType: 'richtext',
      cardNumber: 1,
      order: 3,
      visible: true,
    },
    {
      fieldId: 'domain',
      fieldName: 'Domain',
      fieldType: 'select',
      cardNumber: 2,
      order: 1,
      visible: true,
    },
    {
      fieldId: 'category',
      fieldName: 'Category',
      fieldType: 'select',
      cardNumber: 2,
      order: 2,
      visible: true,
    },
    {
      fieldId: 'control_owner',
      fieldName: 'Control Owner',
      fieldType: 'person',
      cardNumber: 2,
      order: 3,
      visible: true,
    },
  ];
};

const getTemplateFields = (templateType: string): string[] => {
  const templates: Record<string, string[]> = {
    all: [
      'control_id',
      'control_title',
      'control_definition',
      'control_domain',
      'control_category',
      'control_owner',
      'status',
      'justification_for_inclusion',
      'framework',
      'control_requirement',
      'current_maturity',
      'target_maturity',
      'gap_to_target',
      'maturity_target_date',
      'control_effectiveness',
      'control_improvement',
      'implementation_status',
      'implementation_date',
      'implementation_notes',
      'test_frequency',
      'last_test_date',
      'next_test_date',
      'business_function',
    ],
    basic: [
      'control_id',
      'control_title',
      'control_definition',
      'control_domain',
      'control_category',
      'control_owner',
      'status',
    ],
    compliance: [
      'control_id',
      'control_title',
      'control_definition',
      'control_domain',
      'control_category',
      'control_owner',
      'status',
      'framework',
      'control_requirement',
      'test_frequency',
      'last_test_date',
      'next_test_date',
      'implementation_status',
      'implementation_date',
      'implementation_notes',
    ],
    maturity: [
      'control_id',
      'control_title',
      'control_definition',
      'control_domain',
      'control_category',
      'control_owner',
      'status',
      'current_maturity',
      'target_maturity',
      'gap_to_target',
      'maturity_target_date',
      'control_effectiveness',
      'control_improvement',
    ],
  };
  return templates[templateType] || [];
};

const getTemplateCardMapping = (fieldId: string): { card: number; order: number } => {
  const cardMapping: Record<string, { card: number; order: number }> = {
    control_id: { card: 1, order: 1 },
    control_title: { card: 1, order: 2 },
    control_definition: { card: 1, order: 3 },
    status: { card: 1, order: 4 },
    control_domain: { card: 2, order: 1 },
    control_category: { card: 2, order: 2 },
    framework: { card: 2, order: 3 },
    current_maturity: { card: 3, order: 1 },
    target_maturity: { card: 3, order: 2 },
    gap_to_target: { card: 3, order: 3 },
    maturity_target_date: { card: 3, order: 4 },
    control_effectiveness: { card: 3, order: 5 },
    implementation_status: { card: 4, order: 1 },
    implementation_date: { card: 4, order: 2 },
    implementation_notes: { card: 4, order: 3 },
    test_frequency: { card: 5, order: 1 },
    last_test_date: { card: 5, order: 2 },
    next_test_date: { card: 5, order: 3 },
    control_frequency: { card: 6, order: 1 },
    control_improvement: { card: 6, order: 2 },
    control_owner: { card: 7, order: 1 },
    business_function: { card: 7, order: 2 },
    control_requirement: { card: 8, order: 1 },
    control_metrics: { card: 9, order: 1 },
  };
  return cardMapping[fieldId] || { card: 1, order: 99 };
};

const defaultTemplateCardNames: Record<string, string> = {
  '1': 'Basic Information',
  '2': 'Classification',
  '3': 'Maturity & Effectiveness',
  '4': 'Implementation',
  '5': 'Testing',
  '6': 'Operational',
  '7': 'Ownership',
  '8': 'Requirements',
  '9': 'Metrics',
};

const sortFields = (fields: FieldConfig[]): FieldConfig[] => {
  return [...fields].sort((a, b) => {
    const ac = Number(a.cardNumber || 0);
    const bc = Number(b.cardNumber || 0);
    if (ac !== bc) return ac - bc;
    return Number(a.order || 0) - Number(b.order || 0);
  });
};

export const ControlConfigPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const matrixId = searchParams.get('matrixId') || '';

  const { data: matrix, isLoading: isLoadingMatrix, error: matrixError } = useQuery({
    queryKey: ['controlMatrix', matrixId],
    queryFn: () => controlMatrixApi.getOne(matrixId),
    enabled: !!matrixId,
  });

  const { data: catalogFieldsRaw = [], isLoading: isLoadingCatalog } = useQuery<CatalogField[]>({
    queryKey: ['catalog', 'control'],
    queryFn: async () => catalogApi.listByCategory('control'),
  });

  const matrixName = (matrix as any)?.name || (matrix as any)?.matrix?.name || matrixId || 'Matrix';

  const fieldConfigsFromApi: FieldConfig[] | undefined =
    (matrix as any)?.matrix?.fields || (matrix as any)?.fields;
  const cardDefinitionsFromApi: Record<string, string> | undefined =
    (matrix as any)?.matrix?.cards || (matrix as any)?.cards;

  const fieldConfigs: FieldConfig[] = useMemo(() => {
    if (Array.isArray(fieldConfigsFromApi) && fieldConfigsFromApi.length > 0) return fieldConfigsFromApi;
    return getDefaultFields();
  }, [fieldConfigsFromApi]);

  const cardDefinitions: Record<string, string> = useMemo(() => {
    if (cardDefinitionsFromApi && typeof cardDefinitionsFromApi === 'object') {
      return cardDefinitionsFromApi;
    }
    return {
      '1': 'Control Information',
      '2': 'Classification & Ownership',
    };
  }, [cardDefinitionsFromApi]);

  const catalogFields: CatalogField[] = useMemo(() => {
    const configuredFieldIds = new Set(fieldConfigs.map((f) => f.fieldId));
    return (catalogFieldsRaw || [])
      .filter((f) => {
        if (!f?.fieldId) return false;
        return (
          f.category === 'control' ||
          f.fieldId.includes('control') ||
          ['domain', 'category', 'status', 'framework'].includes(f.fieldId)
        );
      })
      .filter((f) => !configuredFieldIds.has(f.fieldId))
      .sort((a, b) => (a.fieldName || a.fieldId).localeCompare(b.fieldName || b.fieldId));
  }, [catalogFieldsRaw, fieldConfigs]);

  const [showAddField, setShowAddField] = useState(false);
  const [showManageCards, setShowManageCards] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showEditField, setShowEditField] = useState(false);
  const [showDeleteField, setShowDeleteField] = useState(false);
  const [showEditCard, setShowEditCard] = useState(false);

  const [addFieldSelectedIds, setAddFieldSelectedIds] = useState<string[]>([]);
  const [addFieldCardNumber, setAddFieldCardNumber] = useState<number>(1);
  const [addFieldOrder, setAddFieldOrder] = useState<number>(0);
  const [addFieldRequired, setAddFieldRequired] = useState<boolean>(false);
  const [addFieldVisible, setAddFieldVisible] = useState<boolean>(true);

  const [editFieldId, setEditFieldId] = useState<string>('');
  const [editFieldCardNumber, setEditFieldCardNumber] = useState<number>(1);
  const [editFieldOrder, setEditFieldOrder] = useState<number>(0);
  const [editFieldRequired, setEditFieldRequired] = useState<boolean>(false);
  const [editFieldVisible, setEditFieldVisible] = useState<boolean>(true);

  const [deleteFieldId, setDeleteFieldId] = useState<string>('');
  const deleteFieldName = useMemo(() => {
    const f = fieldConfigs.find((x) => x.fieldId === deleteFieldId);
    return f?.fieldName || f?.fieldId || '';
  }, [deleteFieldId, fieldConfigs]);

  const [templateType, setTemplateType] = useState<'all' | 'basic' | 'compliance' | 'maturity'>('all');

  const [addCardMode, setAddCardMode] = useState(false);
  const [newCardNumber, setNewCardNumber] = useState<number>(1);
  const [newCardName, setNewCardName] = useState<string>('');

  const [editCardNumber, setEditCardNumber] = useState<number>(1);
  const [editCardName, setEditCardName] = useState<string>('');

  const persistMatrix = async (patch: any) => {
    await controlMatrixApi.update(matrixId, patch);
    await queryClient.invalidateQueries({ queryKey: ['controlMatrix', matrixId] });
  };

  const onBackToControls = () => {
    navigate(`/controls?matrixId=${encodeURIComponent(matrixId)}`);
  };

  const openEditField = (field: FieldConfig) => {
    setEditFieldId(field.fieldId);
    setEditFieldCardNumber(Number(field.cardNumber || 1));
    setEditFieldOrder(Number(field.order || 0));
    setEditFieldRequired(Boolean(field.required));
    setEditFieldVisible(field.visible !== false);
    setShowEditField(true);
  };

  const saveEditField = async () => {
    const updated = fieldConfigs.map((f) =>
      f.fieldId === editFieldId
        ? {
            ...f,
            cardNumber: editFieldCardNumber,
            order: editFieldOrder,
            required: editFieldRequired,
            visible: editFieldVisible,
          }
        : f
    );
    await persistMatrix({ fields: updated });
    setShowEditField(false);
  };

  const confirmRemoveField = async () => {
    const updated = fieldConfigs.filter((f) => f.fieldId !== deleteFieldId);
    await persistMatrix({ fields: updated });
    setShowDeleteField(false);
    toast.success('Field removed.');
  };

  const clearAllFields = async () => {
    const ok = await swal.confirm('Remove all fields from this matrix? This cannot be undone.');
    if (!ok) return;
    await persistMatrix({ fields: [] });
    toast.success('All fields have been cleared');
  };

  const addFields = async () => {
    if (addFieldSelectedIds.length === 0 || !addFieldCardNumber) {
      toast.warning('Please select at least one field and specify a card number');
      return;
    }

    let addedCount = 0;
    const existing = new Set(fieldConfigs.map((f) => f.fieldId));
    const additions: FieldConfig[] = [];

    for (const fieldId of addFieldSelectedIds) {
      if (existing.has(fieldId)) continue;
      const cf = catalogFieldsRaw.find((f: any) => f.fieldId === fieldId);
      if (!cf) continue;
      additions.push({
        fieldId,
        fieldName: cf.fieldName,
        fieldType: cf.fieldType,
        options: cf.options || [],
        cardNumber: addFieldCardNumber,
        order: addFieldOrder + addedCount,
        required: addFieldRequired,
        visible: addFieldVisible,
      });
      addedCount += 1;
    }

    if (addedCount === 0) {
      toast.info('No new fields were added');
      return;
    }

    const updated = [...fieldConfigs, ...additions];
    await persistMatrix({ fields: updated });
    setShowAddField(false);
    setAddFieldSelectedIds([]);
    toast.success(`${addedCount} field(s) added.`);
  };

  const applyTemplate = async () => {
    const templateFields = getTemplateFields(templateType);
    const configured = new Set(fieldConfigs.map((f) => f.fieldId));
    const toAdd = templateFields.filter((id) => !configured.has(id));

    if (toAdd.length === 0) {
      toast.info('All fields from this template are already configured');
      setShowTemplate(false);
      return;
    }

    const newFields: FieldConfig[] = [];
    for (const fieldId of toAdd) {
      const cf = catalogFieldsRaw.find((f: any) => f.fieldId === fieldId);
      if (!cf) continue;
      const cardInfo = getTemplateCardMapping(fieldId);
      newFields.push({
        fieldId,
        fieldName: cf.fieldName,
        fieldType: cf.fieldType,
        options: cf.options || [],
        cardNumber: cardInfo.card,
        order: cardInfo.order,
        required: ['control_id', 'control_title', 'control_owner'].includes(fieldId),
        visible: true,
      });
    }

    const updatedFields = [...fieldConfigs, ...newFields];
    await persistMatrix({ fields: updatedFields, cards: defaultTemplateCardNames });
    setShowTemplate(false);
    toast.success(`Template applied. Added ${newFields.length} field(s).`);
  };

  const openManageCards = () => {
    setShowManageCards(true);
    setAddCardMode(false);
    const existingNumbers = Object.keys(cardDefinitions)
      .map((n) => parseInt(n, 10))
      .filter((n) => Number.isFinite(n));
    const next = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    setNewCardNumber(next);
    setNewCardName('');
  };

  const saveCards = async (nextCards: Record<string, string>) => {
    await persistMatrix({ cards: nextCards });
  };

  const addCard = async () => {
    if (!newCardNumber || !newCardName.trim()) {
      toast.warning('Please provide both card number and name');
      return;
    }
    if (cardDefinitions[String(newCardNumber)]) {
      toast.error('A card with this number already exists. Please use a different number or edit the existing card.');
      return;
    }
    const nextCards = { ...cardDefinitions, [String(newCardNumber)]: newCardName.trim() };
    await saveCards(nextCards);
    setAddCardMode(false);
  };

  const openEditCard = (cardNumber: number) => {
    setEditCardNumber(cardNumber);
    setEditCardName(cardDefinitions[String(cardNumber)] || '');
    setShowEditCard(true);
  };

  const updateCard = async () => {
    if (!editCardName.trim()) {
      toast.warning('Please provide a card name');
      return;
    }
    const nextCards = { ...cardDefinitions, [String(editCardNumber)]: editCardName.trim() };
    await saveCards(nextCards);
    setShowEditCard(false);
  };

  const deleteCard = async (cardNumber: number) => {
    const fieldsUsing = fieldConfigs.filter((f) => Number(f.cardNumber) === cardNumber);
    if (fieldsUsing.length > 0) {
      toast.error(
        `Cannot delete this card. ${fieldsUsing.length} field(s) are currently assigned to it. Please reassign these fields first.`
      );
      return;
    }
    const ok = await swal.confirmDelete(`card ${cardNumber} - ${cardDefinitions[String(cardNumber)]}`);
    if (!ok) return;
    const nextCards = { ...cardDefinitions };
    delete nextCards[String(cardNumber)];
    await saveCards(nextCards);
  };

  const isLoading = isLoadingMatrix || isLoadingCatalog;

  if (!matrixId) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <div>
            No matrix selected. Return to <a href="/control-matrices">Control Matrices</a>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-settings-3-line"
        title="Configure Control Fields"
        breadcrumbs={[
          { label: 'Home', to: '/dashboard' },
          { label: 'Matrices', to: '/control-matrices' },
          { label: matrixName, to: `/controls?matrixId=${encodeURIComponent(matrixId)}` },
          { label: 'Configure Fields' },
        ]}
        actions={
          <>
            <button className="np-btn np-btn-ghost" onClick={onBackToControls}>
              <i className="ri-arrow-left-line"></i> Back
            </button>
            <button className="np-btn np-btn-info" onClick={openManageCards}>
              <i className="ri-layout-grid-line"></i> Manage Cards
            </button>
            <button className="np-btn np-btn-success" onClick={() => setShowAddField(true)}>
              <i className="ri-add-line"></i> Add Field
            </button>
            <button className="np-btn np-btn-primary" onClick={() => setShowTemplate(true)}>
              <i className="ri-file-copy-line"></i> Apply Template
            </button>
            <button className="np-btn np-btn-ghost-danger" onClick={clearAllFields}>
              <i className="ri-delete-bin-line"></i> Clear All
            </button>
          </>
        }
      />

      <div className="np-banner np-tone-info mb-4">
        <i className="ri-information-line"></i>
        <div>
          <strong>Field Configuration</strong>
          <br />
          Control which fields appear when creating or editing controls. Organize fields into cards for better structure.
        </div>
      </div>

      {matrixError ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load matrix.</div>
      ) : isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="np-card">
          <div className="np-card-body">
            <NimbusTable thead={<tr>
                    <th>Field Name</th>
                    <th>Card</th>
                    <th>Order</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Visible</th>
                    <th>Actions</th>
                  </tr>}>
                  {fieldConfigs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center np-sub">
                        <i className="ri-list-settings-line me-1"></i>No fields configured
                      </td>
                    </tr>
                  ) : (
                    sortFields(fieldConfigs).map((field) => {
                      const cardName =
                        cardDefinitions[String(field.cardNumber)] || `Card ${field.cardNumber || 1}`;
                      return (
                        <tr key={field.fieldId}>
                          <td>
                            <strong>{field.fieldName || field.fieldId}</strong>
                          </td>
                          <td>
                            <span className="np-chip np-tone-primary">{cardName}</span>
                          </td>
                          <td className="np-tabular">{field.order || 0}</td>
                          <td>
                            <code>{field.fieldType || 'text'}</code>
                          </td>
                          <td>
                            {field.required ? (
                              <i className="ri-checkbox-circle-line text-success"></i>
                            ) : (
                              <i className="ri-close-circle-line text-muted"></i>
                            )}
                          </td>
                          <td>
                            {field.visible !== false ? (
                              <i className="ri-eye-line text-primary"></i>
                            ) : (
                              <i className="ri-eye-off-line text-muted"></i>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="np-icon-btn"
                                onClick={() => openEditField(field)}
                                title="Edit"
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                              <button
                                className="np-icon-btn np-icon-btn-danger"
                                onClick={() => {
                                  setDeleteFieldId(field.fieldId);
                                  setShowDeleteField(true);
                                }}
                                title="Remove"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
            </NimbusTable>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      {showAddField && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title="Add Field"
            onClose={() => setShowAddField(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowAddField(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={addFields} disabled={isLoadingCatalog}>
                  Add Field
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Select Fields from Catalog</label>
                  <select
                    className="np-select"
                    multiple
                    size={12}
                    value={addFieldSelectedIds}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setAddFieldSelectedIds(options);
                    }}
                  >
                    {catalogFields.map((f) => (
                      <option key={f.fieldId} value={f.fieldId}>
                        {(f.fieldName || f.fieldId) + ` (${f.fieldType || 'text'})`}
                      </option>
                    ))}
                  </select>
                  <small className="np-hint">{catalogFields.length} fields available</small>
                </div>

                <div className="np-field">
                  <label>Card Number <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="np-input np-tabular"
                    min={1}
                    value={addFieldCardNumber}
                    onChange={(e) => setAddFieldCardNumber(parseInt(e.target.value, 10) || 1)}
                  />
                  <small className="np-hint">Group fields into cards (1, 2, 3, etc.)</small>
                </div>

                <div className="np-field">
                  <label>Display Order</label>
                  <input
                    type="number"
                    className="np-input np-tabular"
                    min={0}
                    value={addFieldOrder}
                    onChange={(e) => setAddFieldOrder(parseInt(e.target.value, 10) || 0)}
                  />
                  <small className="np-hint">Lower numbers appear first</small>
                </div>

                <div className="d-flex align-items-center gap-2 mb-3">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={addFieldRequired}
                      onChange={(e) => setAddFieldRequired(e.target.checked)}
                      id="fieldRequired"
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="fieldRequired" className="mb-0">
                    Required Field
                  </label>
                </div>

                <div className="d-flex align-items-center gap-2 mb-3">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={addFieldVisible}
                      onChange={(e) => setAddFieldVisible(e.target.checked)}
                      id="fieldVisible"
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="fieldVisible" className="mb-0">
                    Visible
                  </label>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Edit Field Modal */}
      {showEditField && (
        <NimbusModal>
          <NimbusModalContent
            title="Edit Field"
            onClose={() => setShowEditField(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowEditField(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={saveEditField}>
                  Save
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Card Number</label>
                  <input
                    type="number"
                    className="np-input np-tabular"
                    min={1}
                    value={editFieldCardNumber}
                    onChange={(e) => setEditFieldCardNumber(parseInt(e.target.value, 10) || 1)}
                  />
                </div>
                <div className="np-field">
                  <label>Order</label>
                  <input
                    type="number"
                    className="np-input np-tabular"
                    min={0}
                    value={editFieldOrder}
                    onChange={(e) => setEditFieldOrder(parseInt(e.target.value, 10) || 0)}
                  />
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={editFieldRequired}
                      onChange={(e) => setEditFieldRequired(e.target.checked)}
                      id="editFieldRequired"
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="editFieldRequired" className="mb-0">
                    Required Field
                  </label>
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <label className="np-switch mb-0">
                    <input
                      type="checkbox"
                      checked={editFieldVisible}
                      onChange={(e) => setEditFieldVisible(e.target.checked)}
                      id="editFieldVisible"
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor="editFieldVisible" className="mb-0">
                    Visible
                  </label>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Delete Field Modal */}
      {showDeleteField && (
        <NimbusModal>
          <NimbusModalContent
            title="Confirm Delete"
            onClose={() => setShowDeleteField(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowDeleteField(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-danger" onClick={confirmRemoveField}>
                  Remove Field
                </button>
              </>
            }
          >
                <p>
                  Are you sure you want to remove <strong>{deleteFieldName}</strong> from this matrix?
                </p>
                <p className="np-hint mb-0">This will not delete existing data, just hide the field.</p>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Template Modal */}
      {showTemplate && (
        <NimbusModal>
          <NimbusModalContent
            title="Apply Template"
            onClose={() => setShowTemplate(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowTemplate(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={applyTemplate}>
                  Apply Template
                </button>
              </>
            }
          >
                <p>Select a template to apply pre-configured fields to this control matrix.</p>
                <div className="np-field">
                  <label>Template</label>
                  <select
                    className="np-select"
                    value={templateType}
                    onChange={(e) => setTemplateType(e.target.value as any)}
                  >
                    <option value="all">All Fields (recommended)</option>
                    <option value="basic">Basic</option>
                    <option value="compliance">Compliance</option>
                    <option value="maturity">Maturity</option>
                  </select>
                </div>
                <div className="np-banner np-tone-warning">
                  <i className="ri-alert-line"></i>
                  <div>This will add template fields to your existing configuration.</div>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Manage Cards Modal */}
      {showManageCards && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title="Manage Cards"
            onClose={() => setShowManageCards(false)}
            footer={
              <button className="np-btn np-btn-ghost" onClick={() => setShowManageCards(false)}>
                Close
              </button>
            }
          >
                <div className="mb-3">
                  <button className="np-btn np-btn-sm np-btn-primary" onClick={() => setAddCardMode(true)}>
                    <i className="ri-add-line"></i> Add New Card
                  </button>
                </div>

                {addCardMode && (
                  <div className="np-card mb-3">
                    <div className="np-card-head">
                      <h2>Add New Card</h2>
                    </div>
                    <div className="np-card-body">
                      <div className="row">
                        <div className="col-md-4 np-field">
                          <label>Card Number</label>
                          <input
                            type="number"
                            className="np-input np-tabular"
                            min={1}
                            max={20}
                            value={newCardNumber}
                            onChange={(e) => setNewCardNumber(parseInt(e.target.value, 10) || 1)}
                          />
                        </div>
                        <div className="col-md-8 np-field">
                          <label>Card Name</label>
                          <input
                            type="text"
                            className="np-input"
                            value={newCardName}
                            onChange={(e) => setNewCardName(e.target.value)}
                            placeholder="e.g., Custom Section"
                          />
                        </div>
                      </div>
                      <div>
                        <button className="np-btn np-btn-sm np-btn-primary me-2" onClick={addCard}>
                          Add Card
                        </button>
                        <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => setAddCardMode(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <NimbusTable thead={<tr>
                        <th>Card #</th>
                        <th>Card Name</th>
                        <th>Fields</th>
                        <th>Actions</th>
                      </tr>}>
                      {Object.keys(cardDefinitions).length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center np-sub">
                            <i className="ri-layout-grid-line me-1"></i>No cards defined. Click "Add New Card" to create one.
                          </td>
                        </tr>
                      ) : (
                        Object.keys(cardDefinitions)
                          .map((n) => parseInt(n, 10))
                          .filter((n) => Number.isFinite(n))
                          .sort((a, b) => a - b)
                          .map((cardNum) => {
                            const fieldsCount = fieldConfigs.filter(
                              (f) => Number(f.cardNumber) === cardNum
                            ).length;
                            return (
                              <tr key={cardNum}>
                                <td>
                                  <strong className="np-tabular">{cardNum}</strong>
                                </td>
                                <td>{cardDefinitions[String(cardNum)]}</td>
                                <td>
                                  <span className="np-chip np-tone-primary np-tabular">{fieldsCount} field(s)</span>
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <button
                                      className="np-icon-btn"
                                      onClick={() => openEditCard(cardNum)}
                                      title="Edit"
                                    >
                                      <i className="ri-edit-line"></i>
                                    </button>
                                    <button
                                      className="np-icon-btn np-icon-btn-danger"
                                      onClick={() => deleteCard(cardNum)}
                                      title="Delete"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                      )}
                </NimbusTable>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Edit Card Modal */}
      {showEditCard && (
        <NimbusModal>
          <NimbusModalContent
            title="Edit Card"
            onClose={() => setShowEditCard(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowEditCard(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={updateCard}>
                  Save
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Card #</label>
                  <input type="number" className="np-input np-tabular" value={editCardNumber} disabled />
                </div>
                <div className="np-field">
                  <label>Card Name</label>
                  <input
                    type="text"
                    className="np-input"
                    value={editCardName}
                    onChange={(e) => setEditCardName(e.target.value)}
                  />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
